import { useEffect, useRef } from 'react';
import { useBlocker, useNavigate } from 'react-router-dom';
import { apiClient } from '../network/ApiClient';
import { useConfirm, useConfirmChoice } from '../context/ConfirmContext';
import { recordGameExit } from '../debug/exitBreadcrumbs';
import { createLeaveController, shouldBlockLeave } from './leaveDecision';
import type { GameMode } from '../types/game';

interface Params {
  /** Session-global game type. `undefined` → FAILS CLOSED: no destructive
   *  action until the authoritative mode is known (see leaveDecision.ts). */
  gameMode:  GameMode | undefined;
  sessionId: number;
  gameId:    number;
  /** The current game route, e.g. "/game/123" — nav to this path is not guarded. */
  gamePath:  string;
  /**
   * AUTHORITATIVE whole-match completion (roundEndPayload.sessionOver). While
   * true, BOTH protections stand down: nothing can be paused, saved, or
   * forfeited, and the rematch flow owns every exit — its resolution
   * navigation must not be intercepted. False/undefined during live play and
   * ordinary between-round Round End; resets automatically for an accepted
   * rematch's successor game (the payload clears on phaser:next-round-ready).
   */
  matchCompleted?: boolean;
  /**
   * Friend-spectating: the viewer is not a participant, so leaving can never
   * pause/save/forfeit anything and closing the tab has zero consequence.
   * When true, every protection this hook offers stands down completely —
   * navigation away is never blocked and no beforeunload warning fires. The
   * Phaser Leave button (vector 2) still simply navigates home either way.
   * Defaults to false so every existing caller (real participants) is
   * unaffected.
   */
  disabled?: boolean;
}

/**
 * Guards leaving the game screen, with mode-specific consequences.
 *
 * Persistent solo game (1 human + 3 bots): leaving PAUSES and SAVES the game —
 * no leaveSession, no forfeit, no abandon. The backend keeps it active and
 * resumable (Steps 3b–3d).
 * Online multiplayer: leaving FORFEITS — confirm, then `leaveSession`, then
 * navigate; stay on failure.
 * Unknown mode (still loading / fetch failed): FAILS CLOSED — no destructive
 * action is offered; a safe Stay/Try-again modal re-fetches the authoritative
 * mode (see leaveDecision.ts).
 *
 * Covers three exit vectors through one path:
 *   • ALL client-side navigation away from the game — a route-level
 *     `useBlocker` (data router). This is surface-agnostic: desktop sidebar
 *     <Link>, mobile drawer navigate(), top bar, logo, browser back/forward,
 *     and any programmatic navigate() are all caught by the same decision.
 *   • the Phaser-canvas Leave button — the `leave-match-request` window event
 *     simply navigate()s to /home; the blocker above intercepts it too.
 *   • browser tab close / refresh — a NATIVE `beforeunload` warning, MULTIPLAYER
 *     ONLY (a styled modal can't run during unload; solo games persist
 *     server-side so no native prompt is shown for them).
 */
export function useLeaveGameGuard({ gameMode, sessionId, gameId, gamePath, matchCompleted = false, disabled = false }: Params): void {
  const navigate      = useNavigate();
  const confirm       = useConfirm();
  const confirmChoice = useConfirmChoice();
  // One re-entrancy-guarded decision machine per mounted game screen.
  const controllerRef = useRef(createLeaveController());

  // Latest values, read live by the once-registered listeners.
  const stateRef = useRef({ gameMode, sessionId, gameId, gamePath, matchCompleted, disabled });
  stateRef.current = { gameMode, sessionId, gameId, gamePath, matchCompleted, disabled };

  // ── Vector 1: route-level block of EVERY client-side navigation ───────────
  // Blocks any path change away from the game (except back onto the same game
  // page). Covers sidebar links, mobile drawer navigate(), top bar, logo,
  // browser back/forward, and any programmatic navigation — one decision.
  // Stands down entirely once the match is authoritatively complete
  // (matchCompleted), or immediately/always for a spectator (disabled).
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !stateRef.current.disabled &&
      shouldBlockLeave(
        currentLocation.pathname,
        nextLocation.pathname,
        stateRef.current.gamePath,
        stateRef.current.matchCompleted,
      ),
  );

  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    const { gameMode: mode, sessionId: sid, gameId: gid } = stateRef.current;
    // Delegate to the pure controller. It owns re-entrancy (at most one modal),
    // and only ever calls proceed()/stay() — never navigate() — so the router
    // keeps the original destination and the decision cannot loop.
    void controllerRef.current.handleBlocked(
      { mode, sessionId: sid, gameId: gid },
      {
        confirmChoice,
        confirm,
        closeSoloGame:         (s) => apiClient.closeSoloGame(s),
        leaveSession:          (s) => apiClient.leaveSession(s),
        // Unknown-mode fail-closed retry: re-fetch the authoritative mode.
        resolveMode:           async () => (await apiClient.getGameState(gid)).game_mode,
        recordExit:            recordGameExit,
        emitActiveGameChanged: () => window.dispatchEvent(new CustomEvent('active-game-changed')),
        proceed:               () => blocker.proceed?.(),
        stay:                  () => blocker.reset?.(),
      },
    );
  }, [blocker, confirm, confirmChoice]);

  // ── Vector 2: Phaser-canvas Leave button → navigate; the blocker catches it ─
  useEffect(() => {
    const onLeaveRequest = () => navigate('/home');
    window.addEventListener('leave-match-request', onLeaveRequest);
    return () => window.removeEventListener('leave-match-request', onLeaveRequest);
  }, [navigate]);

  // ── Vector 3: tab close / refresh — native warning, MULTIPLAYER only ──────
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      // Spectator: nothing to lose by closing the tab — never warn.
      if (stateRef.current.disabled) return;
      // Solo bot games persist server-side; no native prompt. Unknown mode is
      // treated as multiplayer (conservative) and warns.
      if (stateRef.current.gameMode === 'bots') return;
      // A completed match can no longer be forfeited or lost — closing the
      // tab is harmless, so no warning (approved product decision).
      if (stateRef.current.matchCompleted) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
}
